import React, { useState, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { Plus, MoreHorizontal, X, Settings } from 'lucide-react';
import SheetRowDetail from './SheetRowDetail';
import { normalizeOptions } from '../utils/sheetOptions';

// --- Design tokens ---
const ACCENT = '#667eea';
const TEXT_TITLE = '#1a1a2e';
const TEXT_BODY = '#334155';
const TEXT_MUTED = '#64748b';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';
const LIST_BG = '#f5f7fb';
const COLOR_PALETTE = ['#94a3b8', '#60a5fa', '#34d399', '#fb923c', '#f472b6', '#a78bfa', '#facc15', '#ef4444'];

// --- Helpers ---

function getCellRaw(cell) {
  if (!cell || cell.value == null) return null;
  if (typeof cell.value === 'string') {
    try { return JSON.parse(cell.value); } catch (_e) { return cell.value; }
  }
  return cell.value;
}

function getCellDisplay(cell) {
  if (!cell) return '';
  if (cell.display_val != null) return String(cell.display_val);
  const raw = getCellRaw(cell);
  return raw != null ? String(raw) : '';
}

function parseLabels(cell) {
  // multi_select cell holds a JSON array of option ids
  const raw = getCellRaw(cell);
  if (Array.isArray(raw)) return raw.map(String);
  if (raw == null || raw === '') return [];
  return [String(raw)];
}

function dueDateColor(iso) {
  if (!iso) return null;
  const ymd = String(iso).slice(0, 10);
  const today = new Date();
  const t = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  if (ymd < t) return { bg: '#fee2e2', fg: '#b91c1c' };       // past
  if (ymd === t) return { bg: '#fee2e2', fg: '#b91c1c' };     // today
  // within 2 days
  const d = new Date(ymd + 'T00:00:00');
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 2);
  if (d <= tomorrow) return { bg: '#fef3c7', fg: '#b45309' };
  return { bg: '#f1f5f9', fg: '#475569' };
}

function formatDueDate(iso) {
  if (!iso) return '';
  const [y, m, day] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return iso;
  const d = new Date(y, m - 1, day);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// --- Component ---

function SheetKanbanView({ sheet, user, onReload, onSheetUpdated }) {
  const [draggingRowId, setDraggingRowId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [openMenuListId, setOpenMenuListId] = useState(null);
  const [renamingListId, setRenamingListId] = useState(null);
  const [adding, setAdding] = useState({}); // { [listId]: { open, title } }
  const [addingList, setAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedSprintId, setSelectedSprintId] = useState(null); // null = Backlog
  const [showSprintForm, setShowSprintForm] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null); // sprint object being edited

  const canEdit = sheet.role === 'owner' || sheet.role === 'editor';
  const sprints = useMemo(() => sheet.sprints || [], [sheet.sprints]);

  useEffect(() => {
    axios.get('/api/contacts').then((r) => setContacts(Array.isArray(r.data) ? r.data : [])).catch(() => setContacts([]));
  }, []);

  // If the active sprint disappears (deleted), fall back to Backlog
  useEffect(() => {
    if (selectedSprintId != null && !sprints.some((s) => s.id === selectedSprintId)) {
      setSelectedSprintId(null);
    }
  }, [sprints, selectedSprintId]);

  const columns = sheet.columns || [];

  const allRows = useMemo(() => sheet.rows || [], [sheet.rows]);
  const cells = useMemo(() => sheet.cells || {}, [sheet.cells]);

  // Filter rows by active sprint tab (null sprint_id = backlog)
  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (selectedSprintId == null) return r.sprint_id == null;
      return r.sprint_id === selectedSprintId;
    });
  }, [allRows, selectedSprintId]);

  const titleCol   = columns.find((c) => c.id === sheet.title_column_id);
  const statusCol  = columns.find((c) => c.id === sheet.status_column_id);
  const dueCol     = columns.find((c) => c.id === sheet.due_date_column_id);
  const labelsCol  = columns.find((c) => c.id === sheet.labels_column_id);

  const statusOptions = useMemo(() => normalizeOptions(statusCol?.config?.options), [statusCol]);
  const labelOptions  = useMemo(() => normalizeOptions(labelsCol?.config?.options), [labelsCol]);
  const labelById     = useMemo(() => Object.fromEntries(labelOptions.map((o) => [o.id, o])), [labelOptions]);

  // Group rows by status value, then sort each list by row.position.
  // Rows whose status doesn't match a known option are collected in an
  // "untriaged" bucket so they remain visible and recoverable (drag them
  // into a real list to fix the status).
  const UNTRIAGED_ID = '__untriaged__';
  const { rowsByStatus, untriagedRows } = useMemo(() => {
    const map = new Map();
    statusOptions.forEach((o) => map.set(o.id, []));
    const untriaged = [];
    for (const row of rows) {
      let listId = null;
      if (statusCol) {
        const cell = cells[row.id]?.[statusCol.id];
        const raw = getCellRaw(cell);
        if (raw != null) listId = String(raw);
      }
      if (listId && map.has(listId)) map.get(listId).push(row);
      else untriaged.push(row);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.position - b.position);
    untriaged.sort((a, b) => a.position - b.position);
    return { rowsByStatus: map, untriagedRows: untriaged };
  }, [rows, cells, statusCol, statusOptions]);

  const selectedRow = useMemo(() => {
    if (!selectedCardId) return null;
    const r = rows.find((row) => row.id === selectedCardId);
    if (!r) return null;
    return { ...r, cells: cells[r.id] || {} };
  }, [selectedCardId, rows, cells]);

  // --- Setup banner: no status column at all ---
  if (sheet.setup_required) {
    return (
      <div style={{ padding: '40px 32px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ maxWidth: '480px', background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '1.05rem', color: TEXT_TITLE }}>Set up this plan</h3>
          <p style={{ margin: '0 0 16px', color: TEXT_MUTED, fontSize: '0.9rem' }}>
            Pick or create a Status column so this plan can render as a board.
          </p>
          <button
            type="button"
            onClick={createStatusColumn}
            style={{ padding: '10px 20px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Create a Status column
          </button>
        </div>
      </div>
    );
  }

  async function createStatusColumn() {
    try {
      const res = await axios.post(`/api/sheets/${sheet.id}/columns`, {
        name: 'Status',
        type: 'dropdown',
        config: { options: [
          { id: 'todo', label: 'To do', color: '#94a3b8', position: 0 },
          { id: 'doing', label: 'In progress', color: '#60a5fa', position: 1 },
          { id: 'done', label: 'Done', color: '#34d399', position: 2 }
        ] }
      });
      await axios.put(`/api/sheets/${sheet.id}/roles`, { status_column_id: res.data.id });
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create Status column');
    }
  }

  // --- Mutations ---

  async function saveStatusOptions(nextOptions) {
    try {
      await axios.put(`/api/sheets/columns/${statusCol.id}`, { config: { ...(statusCol.config || {}), options: nextOptions } });
      onReload();
    } catch (err) {
      if (err.response?.status === 409) {
        alert(err.response.data.error || 'List has cards. Move them first.');
      } else {
        alert(err.response?.data?.error || 'Failed to save lists');
      }
    }
  }

  async function renameList(option, newLabel) {
    if (!newLabel.trim() || newLabel === option.label) { setRenamingListId(null); return; }
    const next = statusOptions.map((o) => o.id === option.id ? { ...o, label: newLabel.trim() } : o);
    setRenamingListId(null);
    await saveStatusOptions(next);
  }

  async function addList(label) {
    if (!label.trim()) return;
    const id = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || `list-${Date.now()}`;
    const color = COLOR_PALETTE[statusOptions.length % COLOR_PALETTE.length];
    const next = [...statusOptions, { id, label: label.trim(), color, position: statusOptions.length }];
    setNewListTitle('');
    setAddingList(false);
    await saveStatusOptions(next);
  }

  async function deleteList(option) {
    if (!window.confirm(`Delete list "${option.label}"? Cards in it must be moved first or this will fail.`)) return;
    const next = statusOptions.filter((o) => o.id !== option.id).map((o, i) => ({ ...o, position: i }));
    await saveStatusOptions(next);
  }

  async function changeListColor(option, color) {
    const next = statusOptions.map((o) => o.id === option.id ? { ...o, color } : o);
    setOpenMenuListId(null);
    await saveStatusOptions(next);
  }

  async function moveList(option, direction) {
    const idx = statusOptions.findIndex((o) => o.id === option.id);
    const target = idx + direction;
    if (target < 0 || target >= statusOptions.length) return;
    const next = [...statusOptions];
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((o, i) => (o.position = i));
    setOpenMenuListId(null);
    await saveStatusOptions(next);
  }

  // Quick-add a card to a specific list (in the active sprint, or backlog)
  async function quickAddCard(listId, title) {
    if (!title.trim()) return;
    try {
      const cellsBody = {};
      if (titleCol) cellsBody[titleCol.id] = title.trim();
      if (statusCol) cellsBody[statusCol.id] = listId;
      await axios.post(`/api/sheets/${sheet.id}/rows`, {
        cells: cellsBody,
        sprint_id: selectedSprintId
      });
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add card');
    }
  }

  // --- Sprint mutations ---
  async function createSprint(payload) {
    try {
      const res = await axios.post(`/api/sheets/${sheet.id}/sprints`, payload);
      setShowSprintForm(false);
      setSelectedSprintId(res.data?.id ?? null);
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create sprint');
    }
  }

  async function updateSprint(sprintId, payload) {
    try {
      await axios.put(`/api/sheets/sprints/${sprintId}`, payload);
      setEditingSprint(null);
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update sprint');
    }
  }

  async function deleteSprint(sprintId) {
    if (!window.confirm('Delete this sprint? Cards in it will move back to the Backlog.')) return;
    try {
      await axios.delete(`/api/sheets/sprints/${sprintId}`);
      setEditingSprint(null);
      setSelectedSprintId(null);
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete sprint');
    }
  }

  async function moveCardToSprint(rowId, sprintId) {
    try {
      await axios.put(`/api/sheets/rows/${rowId}`, { sprint_id: sprintId });
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to move card');
    }
  }

  async function deleteCard(rowId) {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    try {
      await axios.delete(`/api/sheets/rows/${rowId}`);
      setSelectedCardId(null);
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete card');
    }
  }

  async function handleCellUpdate(rowId, cellsToUpdate) {
    try {
      await axios.put(`/api/sheets/rows/${rowId}/cells`, { cells: cellsToUpdate });
      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    }
  }

  // Drag handlers
  function handleDragStart(e, rowId) {
    setDraggingRowId(rowId);
    try { e.dataTransfer.setData('text/plain', String(rowId)); e.dataTransfer.effectAllowed = 'move'; } catch (_e) {}
  }
  function handleDragEnd() { setDraggingRowId(null); }
  function handleDragOver(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }

  async function handleDropOnList(listId, targetRowId /* may be null */) {
    if (!draggingRowId || !statusCol) return;
    const row = rows.find((r) => r.id === draggingRowId);
    if (!row) return;

    const cell = cells[row.id]?.[statusCol.id];
    const currentList = cell ? String(getCellRaw(cell)) : null;

    try {
      // 1. If switching list, write the status cell via the bulk-per-row endpoint.
      //    Body shape: { cells: { [columnId]: <value> } }. The server auto-handles display_val.
      if (currentList !== listId) {
        await axios.put(`/api/sheets/rows/${row.id}/cells`, { cells: { [statusCol.id]: listId } });
      }

      // 2. Reorder within the destination list.
      //    PUT /api/sheets/rows/:id auto-shifts other rows on every call, so writes MUST be
      //    sequential — Promise.all would race and corrupt positions.
      const destRows = (rowsByStatus.get(listId) || []).filter((r) => r.id !== row.id);
      const targetIdx = targetRowId ? destRows.findIndex((r) => r.id === targetRowId) : destRows.length;
      const moved = [...destRows.slice(0, targetIdx), row, ...destRows.slice(targetIdx)];
      const writes = moved
        .map((r, i) => ({ id: r.id, position: i }))
        .filter((p) => {
          const existing = rows.find((r) => r.id === p.id);
          return existing && existing.position !== p.position;
        });
      for (const p of writes) {
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`/api/sheets/rows/${p.id}`, { position: p.position });
      }

      onReload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to move card');
    } finally {
      setDraggingRowId(null);
    }
  }

  // --- Render ---

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <SprintTabBar
        sprints={sprints}
        selectedSprintId={selectedSprintId}
        onSelect={setSelectedSprintId}
        onCreate={() => setShowSprintForm(true)}
        onEdit={(sprint) => setEditingSprint(sprint)}
        canEdit={canEdit}
        rows={allRows}
      />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', overflowX: 'auto', padding: '16px 24px', gap: '14px', alignItems: 'flex-start' }}>
      {untriagedRows.length > 0 && (
        <div
          key={UNTRIAGED_ID}
          style={{ flex: '0 0 280px', background: '#fef3c7', border: '1px dashed #f59e0b', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 200px)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>Untriaged</span>
            <span style={{ fontSize: '0.72rem', color: '#92400e', fontWeight: 600, padding: '0 6px', background: '#fde68a', borderRadius: '999px' }}>{untriagedRows.length}</span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#92400e', marginBottom: '8px', lineHeight: 1.3 }}>
            These cards have no valid status. Drag them into a list to fix.
          </div>
          <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '2px' }}>
            {untriagedRows.map((row) => (
              <KanbanCard
                key={row.id}
                row={row}
                titleCol={titleCol}
                dueCol={dueCol}
                labelsCol={labelsCol}
                labelById={labelById}
                cells={cells}
                isDragging={draggingRowId === row.id}
                isSelected={selectedCardId === row.id}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={() => {}}
                onClick={() => setSelectedCardId(row.id)}
              />
            ))}
          </div>
        </div>
      )}
      {statusOptions.map((option) => {
        const listRows = rowsByStatus.get(option.id) || [];
        const isAddOpen = !!adding[option.id]?.open;
        return (
          <div
            key={option.id}
            style={{ flex: '0 0 280px', background: LIST_BG, borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 200px)' }}
            onDragOver={handleDragOver}
            onDrop={() => handleDropOnList(option.id, null)}
          >
            {/* List header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: option.color, flexShrink: 0 }} />
                {renamingListId === option.id ? (
                  <input
                    autoFocus
                    defaultValue={option.label}
                    onBlur={(e) => renameList(option, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setRenamingListId(null); }}
                    style={{ flex: 1, padding: '4px 6px', border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, color: TEXT_TITLE, background: SURFACE }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setRenamingListId(option.id)}
                    style={{ flex: 1, textAlign: 'left', background: 'transparent', border: 'none', padding: '4px 0', cursor: 'text', fontSize: '0.85rem', fontWeight: 600, color: TEXT_TITLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {option.label}
                  </button>
                )}
                <span style={{ fontSize: '0.72rem', color: TEXT_MUTED, fontWeight: 600, padding: '0 6px', background: '#e2e8f0', borderRadius: '999px', flexShrink: 0 }}>{listRows.length}</span>
              </div>
              <button
                type="button"
                onClick={() => setOpenMenuListId(openMenuListId === option.id ? null : option.id)}
                style={{ background: 'transparent', border: 'none', padding: '4px', cursor: 'pointer', color: TEXT_MUTED, display: 'flex' }}
                title="List options"
              >
                <MoreHorizontal size={14} />
              </button>
              {openMenuListId === option.id && (
                <ListMenu
                  option={option}
                  onRename={() => { setRenamingListId(option.id); setOpenMenuListId(null); }}
                  onColor={(color) => changeListColor(option, color)}
                  onMoveLeft={() => moveList(option, -1)}
                  onMoveRight={() => moveList(option, +1)}
                  onDelete={() => { setOpenMenuListId(null); deleteList(option); }}
                  onClose={() => setOpenMenuListId(null)}
                />
              )}
            </div>

            {/* Cards */}
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '2px' }}>
              {listRows.map((row) => (
                <KanbanCard
                  key={row.id}
                  row={row}
                  titleCol={titleCol}
                  dueCol={dueCol}
                  labelsCol={labelsCol}
                  labelById={labelById}
                  cells={cells}
                  isDragging={draggingRowId === row.id}
                  isSelected={selectedCardId === row.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropOnList(option.id, row.id)}
                  onClick={() => setSelectedCardId(row.id)}
                />
              ))}
            </div>

            {/* Quick-add */}
            <QuickAdd
              open={isAddOpen}
              title={adding[option.id]?.title || ''}
              onOpen={() => setAdding({ ...adding, [option.id]: { open: true, title: '' } })}
              onCancel={() => setAdding({ ...adding, [option.id]: { open: false, title: '' } })}
              onChange={(t) => setAdding({ ...adding, [option.id]: { open: true, title: t } })}
              onSubmit={async () => {
                const t = adding[option.id]?.title || '';
                await quickAddCard(option.id, t);
                setAdding({ ...adding, [option.id]: { open: true, title: '' } }); // refocus
              }}
            />
          </div>
        );
      })}

      {/* Add list */}
      <div style={{ flex: '0 0 280px' }}>
        {addingList ? (
          <form
            onSubmit={(e) => { e.preventDefault(); addList(newListTitle); }}
            style={{ background: LIST_BG, borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <input
              autoFocus
              value={newListTitle}
              onChange={(e) => setNewListTitle(e.target.value)}
              placeholder="Enter list title…"
              style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', background: SURFACE }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setAddingList(false); setNewListTitle(''); } }}
            />
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="submit" style={{ padding: '6px 14px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Add list</button>
              <button type="button" onClick={() => { setAddingList(false); setNewListTitle(''); }} style={{ background: 'transparent', border: 'none', padding: '4px', cursor: 'pointer', color: TEXT_MUTED, display: 'flex' }}>
                <X size={16} />
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setAddingList(true)}
            style={{ width: '100%', padding: '12px', background: 'transparent', color: TEXT_MUTED, border: `1px dashed ${BORDER}`, borderRadius: '12px', cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <Plus size={14} /> Add list
          </button>
        )}
      </div>

      {/* Card detail panel */}
      {selectedRow && (
        <SheetRowDetail
          row={selectedRow}
          columns={sheet.columns || []}
          sheet={sheet}
          onCellUpdate={handleCellUpdate}
          onClose={() => setSelectedCardId(null)}
          canEdit={canEdit}
          contacts={contacts}
          currentUser={user}
          sprints={sprints}
          onMoveToSprint={canEdit ? (sprintId) => moveCardToSprint(selectedRow.id, sprintId) : null}
          onDelete={canEdit ? () => deleteCard(selectedRow.id) : null}
        />
      )}
      </div>

      {showSprintForm && (
        <SprintFormModal
          title="New sprint"
          onSubmit={createSprint}
          onClose={() => setShowSprintForm(false)}
        />
      )}

      {editingSprint && (
        <SprintFormModal
          title="Edit sprint"
          sprint={editingSprint}
          onSubmit={(payload) => updateSprint(editingSprint.id, payload)}
          onDelete={() => deleteSprint(editingSprint.id)}
          onClose={() => setEditingSprint(null)}
        />
      )}
    </div>
  );
}

function KanbanCard({ row, titleCol, dueCol, labelsCol, labelById, cells, isDragging, isSelected, onDragStart, onDragEnd, onDragOver, onDrop, onClick }) {
  const title = titleCol ? getCellDisplay(cells[row.id]?.[titleCol.id]) : '(Untitled)';
  const due   = dueCol ? getCellDisplay(cells[row.id]?.[dueCol.id]) : '';
  const labelIds = labelsCol ? parseLabels(cells[row.id]?.[labelsCol.id]) : [];
  const dueChip = due ? dueDateColor(due) : null;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, row.id)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={(e) => { e.stopPropagation(); onDrop(); }}
      onClick={onClick}
      style={{
        background: SURFACE,
        border: isDragging ? `2px solid ${ACCENT}` : (isSelected ? `1.5px solid ${ACCENT}` : `1px solid #e5e7eb`),
        borderRadius: '8px',
        padding: '12px',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        transition: 'border-color 0.15s'
      }}
    >
      {labelIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {labelIds.map((id) => {
            const opt = labelById[id];
            if (!opt) return null;
            return <span key={id} title={opt.label} style={{ display: 'inline-block', width: '32px', height: '6px', background: opt.color, borderRadius: '3px' }} />;
          })}
        </div>
      )}
      <div style={{
        fontSize: '14px', fontWeight: 500, color: '#111827', lineHeight: 1.35,
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
      }}>
        {title || '(Untitled)'}
      </div>
      {due && dueChip && (
        <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: dueChip.bg, color: dueChip.fg, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600 }}>
          {formatDueDate(due)}
        </span>
      )}
    </div>
  );
}

function QuickAdd({ open, title, onOpen, onCancel, onChange, onSubmit }) {
  const inputRef = useRef(null);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        style={{ marginTop: '8px', width: '100%', padding: '8px', background: 'transparent', color: TEXT_MUTED, border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.85rem' }}
      >
        <Plus size={14} /> Add a card
      </button>
    );
  }
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}
    >
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
        placeholder="Enter a title…"
        style={{ padding: '8px 10px', borderRadius: '6px', border: `1px solid ${BORDER}`, fontSize: '0.85rem', background: SURFACE }}
      />
      <div style={{ display: 'flex', gap: '6px' }}>
        <button type="submit" style={{ padding: '6px 14px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>Add card</button>
        <button type="button" onClick={onCancel} style={{ background: 'transparent', border: 'none', padding: '4px', cursor: 'pointer', color: TEXT_MUTED, display: 'flex' }}>
          <X size={14} />
        </button>
      </div>
    </form>
  );
}

function ListMenu({ option, onRename, onColor, onMoveLeft, onMoveRight, onDelete, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
      <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 31, background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', padding: '6px', minWidth: '180px', display: 'flex', flexDirection: 'column' }}>
        <MenuItem onClick={onRename}>Rename list</MenuItem>
        <div style={{ padding: '6px 10px' }}>
          <div style={{ fontSize: '0.7rem', color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Color</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {COLOR_PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => onColor(c)} title={c} style={{ width: '20px', height: '20px', borderRadius: '50%', background: c, border: option.color === c ? '2px solid #111827' : 'none', cursor: 'pointer', padding: 0 }} />
            ))}
          </div>
        </div>
        <div style={{ height: '1px', background: BORDER, margin: '4px 0' }} />
        <MenuItem onClick={onMoveLeft}>Move list left</MenuItem>
        <MenuItem onClick={onMoveRight}>Move list right</MenuItem>
        <div style={{ height: '1px', background: BORDER, margin: '4px 0' }} />
        <MenuItem onClick={onDelete} danger>Delete list…</MenuItem>
      </div>
    </>
  );
}

function MenuItem({ onClick, children, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', textAlign: 'left', padding: '7px 10px', background: 'transparent', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', color: danger ? '#b91c1c' : TEXT_BODY }}
    >
      {children}
    </button>
  );
}

// --- Sprint UI ---

const STATUS_STYLES = {
  planned: { bg: '#e0e7ff', fg: '#3730a3', label: 'Planned' },
  active:  { bg: '#dcfce7', fg: '#166534', label: 'Active' },
  closed:  { bg: '#e5e7eb', fg: '#475569', label: 'Closed' }
};

function formatDateRange(start, end) {
  const fmt = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  if (!start && !end) return '';
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  return fmt(start || end);
}

function SprintTabBar({ sprints, selectedSprintId, onSelect, onCreate, onEdit, canEdit, rows }) {
  // Card counts per sprint for the chip
  const counts = useMemo(() => {
    const m = { backlog: 0 };
    for (const r of rows) {
      if (r.sprint_id == null) m.backlog += 1;
      else m[r.sprint_id] = (m[r.sprint_id] || 0) + 1;
    }
    return m;
  }, [rows]);

  const tabs = [{ id: null, name: 'Backlog', isBacklog: true }, ...sprints];

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '4px',
      padding: '10px 24px 0',
      borderBottom: `1px solid ${BORDER}`,
      background: SURFACE,
      overflowX: 'auto', flexShrink: 0
    }}>
      {tabs.map((tab) => {
        const isActive = (tab.id ?? null) === (selectedSprintId ?? null);
        const count = tab.isBacklog ? counts.backlog : (counts[tab.id] || 0);
        const statusStyle = !tab.isBacklog ? STATUS_STYLES[tab.status] : null;
        return (
          <div key={tab.isBacklog ? 'backlog' : tab.id} style={{ display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => onSelect(tab.id ?? null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 12px',
                background: 'transparent', border: 'none',
                borderBottom: isActive ? `2px solid ${ACCENT}` : '2px solid transparent',
                color: isActive ? TEXT_TITLE : TEXT_MUTED,
                fontSize: '0.85rem', fontWeight: isActive ? 700 : 500,
                cursor: 'pointer', whiteSpace: 'nowrap',
                marginBottom: '-1px'
              }}
            >
              {tab.name}
              {statusStyle && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px',
                  background: statusStyle.bg, color: statusStyle.fg, borderRadius: '999px',
                  textTransform: 'uppercase', letterSpacing: '0.04em'
                }}>{statusStyle.label}</span>
              )}
              <span style={{
                fontSize: '0.7rem', fontWeight: 600,
                padding: '0 6px', background: '#e2e8f0', color: TEXT_MUTED,
                borderRadius: '999px'
              }}>{count}</span>
              {!tab.isBacklog && canEdit && isActive && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onEdit(tab); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onEdit(tab); } }}
                  title="Sprint settings"
                  style={{ display: 'inline-flex', padding: '2px', color: TEXT_MUTED, cursor: 'pointer' }}
                >
                  <Settings size={12} />
                </span>
              )}
            </button>
          </div>
        );
      })}
      {canEdit && (
        <button
          type="button"
          onClick={onCreate}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '6px 10px', marginLeft: '4px',
            background: 'transparent', border: 'none',
            color: ACCENT, fontSize: '0.85rem', fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap'
          }}
          title="Create sprint"
        >
          <Plus size={14} /> New sprint
        </button>
      )}
    </div>
  );
}

function SprintFormModal({ title, sprint, onSubmit, onDelete, onClose }) {
  const [name, setName] = useState(sprint?.name || '');
  const [goal, setGoal] = useState(sprint?.goal || '');
  const [startDate, setStartDate] = useState(sprint?.start_date ? String(sprint.start_date).slice(0, 10) : '');
  const [endDate, setEndDate] = useState(sprint?.end_date ? String(sprint.end_date).slice(0, 10) : '');
  const [status, setStatus] = useState(sprint?.status || 'planned');

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { alert('Sprint name is required'); return; }
    onSubmit({
      name: name.trim(),
      goal: goal.trim() || null,
      start_date: startDate || null,
      end_date: endDate || null,
      status
    });
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      <form
        onSubmit={handleSubmit}
        style={{ width: '100%', maxWidth: '480px', background: SURFACE, borderRadius: '14px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}
      >
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: TEXT_TITLE }}>{title}</h3>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: TEXT_BODY, fontWeight: 600 }}>
          Name
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint 12 — Onboarding polish"
            style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontWeight: 400 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: TEXT_BODY, fontWeight: 600 }}>
          Goal
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="What does success look like?"
            rows={3}
            style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontWeight: 400, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </label>

        <div style={{ display: 'flex', gap: '10px' }}>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: TEXT_BODY, fontWeight: 600 }}>
            Start date
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontWeight: 400 }}
            />
          </label>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: TEXT_BODY, fontWeight: 600 }}>
            End date
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontWeight: 400 }}
            />
          </label>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: TEXT_BODY, fontWeight: 600 }}>
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', fontWeight: 400, background: SURFACE }}
          >
            <option value="planned">Planned</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </label>

        {sprint?.start_date || sprint?.end_date ? (
          <div style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>{formatDateRange(sprint.start_date, sprint.end_date)}</div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
          {onDelete ? (
            <button type="button" onClick={onDelete} style={{ padding: '8px 14px', background: 'transparent', color: '#b91c1c', border: `1px solid #fecaca`, borderRadius: '999px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
              Delete sprint
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: '999px', fontSize: '0.9rem', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" style={{ padding: '8px 18px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>{sprint ? 'Save' : 'Create'}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SheetKanbanView;
